import { ConnectHeader } from '@/pages/company/common/ConnectHeader';
import { Modal as ModalContainer, Box, useTheme, styled } from '@mui/material';
import { ConfirmationBox } from '@/pages/company/componants/Base';
import { useTranslation } from 'react-i18next';
import { useContext, useState, useEffect } from 'react';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { TipsHistory } from '@/services/PaymentsServiceV2';
import { DATE_FORMAT } from '@/lib/constants';
import { useFormatting } from '@/utils/formatting';
import {
    SMMedium,
    SMRegular,
    SMSemiBold,
    SubTitle
} from '@/typography/Typography';
import { UserContext } from '@/contexts/user/UserContext';
import { InputText } from '@/components/shared/InputText';
import {
    PrimaryButtonLG,
    SecondaryButtonGrayLG
} from '@/components/shared/Button';
import { isValidNumber } from '@/utils/validationHelpers';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { RadioButton } from '@/components/shared/RadioButton';
import { CheckedCircle } from '@/icons/CheckedCircle';

const JournalOption = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // width: '402px',
    height: '36px',
    padding: '8px',
    gap: '4px',
    border: `1px solid ${theme.palette.greyBorder}`,
    borderRadius: '8px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    opacity: 1,
    '&:hover': {
        backgroundColor: theme.palette.grey[50],
        borderColor: theme.palette.primary.main
    },
    '&.selected': {
        backgroundColor: theme.palette.primary.lighter || '#F0F7FF',
        borderColor: theme.palette.primary.main
    }
}));

const JournalInfo = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    flex: 1
});

interface EditTipsDialogProp {
    editTip: TipsHistory | null;
    onClose?: () => void;
}

export const EditTipsDialog = ({ editTip, onClose }: EditTipsDialogProp) => {
    const { t } = useTranslation();

    const { updateJournalAmount } = usePaymentsMutations();

    const [newAmount, setNewAmount] = useState<number | undefined>();
    const [inputValue, setInputValue] = useState<string>('');
    const [selectedJournalId, setSelectedJournalId] = useState<string>('');

    // Check journal counts
    const hasJournalAmounts =
        editTip?.posJournalAmounts && Array.isArray(editTip.posJournalAmounts);
    const hasSingleJournal =
        hasJournalAmounts && editTip.posJournalAmounts!.length === 1;
    const hasMultipleJournals =
        hasJournalAmounts && editTip.posJournalAmounts!.length > 1;

    // Auto-select first journal if only one exists
    useEffect(() => {
        if (hasSingleJournal) {
            const journal = editTip.posJournalAmounts![0];
            setSelectedJournalId(journal.posJournalId);
        } else if (hasMultipleJournals) {
            // Reset when showing radio buttons
            setSelectedJournalId('');
            setNewAmount(undefined);
            setInputValue('');
            return;
        }
        if (editTip) {
            setNewAmount(editTip.totalTips);
            setInputValue(editTip.totalTips.toString());
        }
    }, [editTip, hasSingleJournal, hasMultipleJournals]);

    // Handle journal selection from radio button
    const handleJournalChange = (journalId: string) => {
        setSelectedJournalId(journalId);
        const journal = editTip?.posJournalAmounts?.find(
            (j) => j.posJournalId === journalId
        );
        const defaultAmount = journal?.totalTips;
        setNewAmount(defaultAmount);
        setInputValue(
            defaultAmount !== undefined ? defaultAmount.toString() : ''
        );
        updateJournalAmount.reset();
    };

    const onCloseHandler = () => {
        setNewAmount(undefined);
        setInputValue('');
        setSelectedJournalId('');
        updateJournalAmount.reset();
        onClose?.();
    };

    const { getBalanceLabel } = useFormatting();
    const { company } = useContext(UserContext);

    useHandleResponse({
        error: null, // handled outside
        isSuccess: updateJournalAmount.isSuccess,
        successAlertMessage: t('tips.redistribution_successful'),
        isSuccessCallBack: () => {
            onCloseHandler();
        }
    });

    const theme = useTheme();

    return (
        <ModalContainer open={!!editTip} onClose={onCloseHandler}>
            <ConfirmationBox style={{ width: '450px' }}>
                <ConnectHeader
                    // logo={getLogoForPOSProvider()}
                    title={t('tips.edit_tips_for')}
                    description={t('tips.edit_tips_for_description', {
                        date: editTip?.date?.format(
                            DATE_FORMAT.DAY_NAME_MONTH_NAME_DAY_YEAR
                        ),
                        locationName: editTip?.locationName
                    })}
                />

                <SMMedium style={{ textAlign: 'center' }} mb={3} mt={2}>
                    {t('tips.current_tips_amount_label')}{' '}
                    {getBalanceLabel(editTip?.totalTips, company?.currency)}
                </SMMedium>

                {hasMultipleJournals && (
                    <Box mb={3}>
                        <SMMedium mb={1.5}>
                            {t('tips.edit_what_journal')}
                        </SMMedium>
                        {editTip.posJournalAmounts!.map((journal, index) => {
                            const isSelected =
                                selectedJournalId === journal.posJournalId;
                            const journalLabel = `${t(
                                'tips.journal'
                            )} ${String.fromCharCode(65 + index)}`;

                            return (
                                <JournalOption
                                    key={journal.posJournalId}
                                    className={isSelected ? 'selected' : ''}
                                    onClick={() =>
                                        handleJournalChange(
                                            journal.posJournalId
                                        )
                                    }
                                >
                                    <JournalInfo>
                                        <SMSemiBold color="#667085">
                                            {journalLabel}
                                        </SMSemiBold>
                                    </JournalInfo>
                                    <Box display="flex" alignItems="center">
                                        <SMRegular color="text.secondary">
                                            {getBalanceLabel(
                                                journal.totalTips,
                                                company?.currency
                                            )}
                                        </SMRegular>
                                        <RadioButton
                                            checked={isSelected}
                                            onChange={() =>
                                                handleJournalChange(
                                                    journal.posJournalId
                                                )
                                            }
                                            size="small"
                                            customRadio
                                        />
                                    </Box>
                                </JournalOption>
                            );
                        })}
                    </Box>
                )}

                <InputText
                    error={updateJournalAmount.isError}
                    value={inputValue}
                    disabled={hasMultipleJournals && !selectedJournalId}
                    placeholder={t('tips.enter_new_amount')}
                    onChange={(e) => {
                        const value = e.target.value;
                        setInputValue(value);

                        if (value === '' || value === '-') {
                            setNewAmount(undefined);
                        } else if (isValidNumber(value)) {
                            setNewAmount(parseFloat(value));
                        } else {
                            setNewAmount(undefined);
                        }
                    }}
                    label={
                        hasMultipleJournals && !selectedJournalId
                            ? t('tips.select_journal_to_edit')
                            : hasMultipleJournals && selectedJournalId
                            ? `${t('tips.new_tips_amount')} for ${t(
                                  'tips.journal'
                              )} ${String.fromCharCode(
                                  65 +
                                      editTip.posJournalAmounts!.findIndex(
                                          (j) =>
                                              j.posJournalId ===
                                              selectedJournalId
                                      )
                              )}`
                            : t('tips.new_tips_amount')
                    }
                />
                {updateJournalAmount.error ? (
                    <SubTitle color={'error'}>
                        {t('tips.error_cant_distribute')}
                    </SubTitle>
                ) : (
                    <SubTitle>{t('tips.editing_before_payroll')}</SubTitle>
                )}
                <Box
                    m={'20px 0'}
                    display={'flex'}
                    justifyContent={'space-between'}
                >
                    <SecondaryButtonGrayLG
                        onClick={onCloseHandler}
                        sx={{ width: '48%' }}
                    >
                        <SMSemiBold>{t('cancel')}</SMSemiBold>
                    </SecondaryButtonGrayLG>

                    <PrimaryButtonLG
                        loading={updateJournalAmount.isPending}
                        disabled={
                            newAmount === undefined ||
                            updateJournalAmount.isError ||
                            (hasMultipleJournals && !selectedJournalId)
                        }
                        onClick={() => {
                            const journalId = hasMultipleJournals
                                ? selectedJournalId
                                : editTip?.posJournalId;
                            updateJournalAmount.mutate({
                                posJournalId: journalId as string,
                                newTotalAmount: newAmount!.toString()
                            });
                        }}
                        sx={{ width: '48%' }}
                    >
                        <SMSemiBold>{t('tips.redistribute')}</SMSemiBold>
                    </PrimaryButtonLG>
                </Box>
            </ConfirmationBox>
        </ModalContainer>
    );
};
